import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import GenerationForm from '../components/GenerationForm';
import GeneratedCards from '../components/GeneratedCards';
import PricingModal from '../components/PricingModal';
import SupportModal from '../components/SupportModal';
import ShareModal from '../components/ShareModal';
import GoldFeatures from '../components/GoldFeatures';
import QuoteOfTheDay from '../components/QuoteOfTheDay';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { LifeBuoy, Share2, Gift, Loader2, BookMarked, X, Library, Crown, Rocket, FileText, FolderPlus, UploadCloud, Trash2, FolderOpen, ChevronRight, Wand2, Copy, CalendarClock, Plus, Save, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { exportToPDF, exportMSToPDF } from '../utils/exportPDF';
import Joyride, { STATUS } from 'react-joyride';

const AGES_MS = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', 'Universitario / Superior'];

export default function Dashboard() {
  const { user, updateCredits, refreshUser } = useAuth();
  const [generatedResult, setGeneratedResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showShare, setShowShare] = useState(false); 
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [rewardsList, setRewardsList] = useState(null);
  const [isGeneratingRewards, setIsGeneratingRewards] = useState(false);

  const isUltra = ['ultra', 'institution_ultra'].includes(user?.plan);
  const [isRepoOpen, setIsRepoOpen] = useState(false);
  const [showUltraPromo, setShowUltraPromo] = useState(false);
  const [repoItems, setRepoItems] = useState([]);
  const [isUploadingRepo, setIsUploadingRepo] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState({});
  const repoFileRef = useRef(null);

  // ESTADOS PARA LA SECUENCIA MAESTRA
  const [showMSConfig, setShowMSConfig] = useState(false);
  const [showMSResult, setShowMSResult] = useState(false);
  const [isGeneratingMS, setIsGeneratingMS] = useState(false);
  const [msResultData, setMsResultData] = useState(null);
  const [msConfig, setMsConfig] = useState({ annual_plan_id: '', folder_name: 'General', subject: '', age: '14' });
  const [isSavingMS, setIsSavingMS] = useState(false);
  const [msSavedId, setMsSavedId] = useState(null);

  // ESTADOS PARA EL TUTORIAL RE-DISEÑADO
  const [runTour, setRunTour] = useState(false);

  const tourSteps = [
    {
      target: '.tour-step-1',
      disableBeacon: true,
      content: (
        <div className="text-left">
          <p className="text-sm leading-relaxed">
            🚀 <strong>¡Bienvenido a la nueva era docente!</strong> Acá nace tu clase. Podés armar desde una dinámica rápida (Plan Gratuito o Standard) hasta secuencias completas combinando múltiples PDFs o links de YouTube y otros archivos (Plan Pro). Ingresá tu tema y dejá que nuestro motor dinamizador haga la magia.
          </p>
        </div>
      ),
    },
    {
      target: 'body',
      placement: 'center',
      content: (
        <div className="text-left">
          <p className="text-sm leading-relaxed">
            📄 <strong>Tu Biblioteca Personal.</strong> Una vez generada la clase, te aparecerán botones para <span className="text-blue-400 font-bold">Descargar en PDF</span>, guardar en tu historial o compartir el link con colegas.
          </p>
        </div>
      ),
    },
    {
      target: 'body',
      placement: 'center',
      content: (
        <div className="text-left">
          <p className="text-sm leading-relaxed">
            🎁 <strong>Gamificación Activa.</strong> Motivá a tus alumnos creando un <span className="text-amber-400 font-bold">Sistema de Recompensas</span> único basado específicamente en el tema que vas a dar.
          </p>
        </div>
      ),
    },
    {
      target: '.tour-step-4',
      placement: 'top',
      content: (
        <div className="text-left">
          <p className="text-sm leading-relaxed">
            ✨ <strong>Poder GOLD.</strong> ¿Necesitás más? El sistema te permitirá generar automáticamente <span className="text-violet-400 font-bold">Exámenes, Guiones de Video y Resúmenes</span> para ahorrar horas de trabajo administrativo.
          </p>
        </div>
      ),
    },
    {
      target: '.tour-step-5',
      placement: 'left',
      content: (
        <div className="text-left">
          <p className="text-sm leading-relaxed">
            💎 <strong>Cerebro ULTRA.</strong> El nivel máximo. Subí tu <strong>Planificación Anual</strong> y bibliografía completa acá. El motor proyectará tu <span className="text-yellow-400 font-bold">Secuencia Maestra</span> para tener todo el trimestre organizado.
          </p>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const tourCompleted = localStorage.getItem('dinamiclass_tour_completed');
    if (!tourCompleted) {
      setTimeout(() => setRunTour(true), 1500); 
    }
  }, []);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      localStorage.setItem('dinamiclass_tour_completed', 'true'); 
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/history');
      setHistory(data);
    } catch {
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchRepository = useCallback(async () => {
    if (!isUltra) return;
    try {
      const { data } = await api.get('/repository');
      setRepoItems(data);
    } catch {
    }
  }, [isUltra]);

  useEffect(() => {
    refreshUser();
    fetchHistory();
    fetchRepository();
  }, [refreshUser, fetchHistory, fetchRepository]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mpStatus = params.get('mp_status');
    const extRef = params.get('ext_ref');
    if (!mpStatus || !extRef) return;
    window.history.replaceState({}, '', '/');
    if (mpStatus === 'failure') {
      toast.error('El pago no pudo completarse. Intentá de nuevo.');
      return;
    }
    if (mpStatus === 'pending') {
      toast.info('Tu pago está siendo procesado. Te avisaremos cuando se confirme.', { duration: 6000 });
      return;
    }
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 8) {
        toast.error('No pudimos confirmar el pago. Si fue debitado, contactá soporte.');
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${extRef}`);
        if (data.payment_status === 'approved') {
          await refreshUser();
          toast.success(`¡Plan ${data.plan.toUpperCase()} activado!`, { duration: 6000 });
          return;
        }
        setTimeout(() => pollStatus(attempts + 1), 2500);
      } catch {
        setTimeout(() => pollStatus(attempts + 1), 2500);
      }
    };
    toast.loading('Verificando pago...', { id: 'mp-check' });
    pollStatus().then(() => toast.dismiss('mp-check'));
  }, [refreshUser]);

  const handleGenerate = async (formData) => {
    setIsGenerating(true);
    setIsSaved(false);
    setSavedId(null);
    setRewardsList(null); 
    try {
      const { data } = await api.post('/generate', formData);
      setGeneratedResult(data);
      if (data.credits_remaining !== undefined) updateCredits(data.credits_remaining);
    } catch (e) {
      if (e.response?.status === 402) setShowPricing(true);
      else toast.error(e.response?.data?.detail || 'Error al generar.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedResult || isSaved) return;
    setIsSaving(true);
    try {
      const { data } = await api.post('/history', {
        topic: generatedResult.topic,
        age: generatedResult.age,
        subject: generatedResult.subject, 
        level: generatedResult.level,     
        career: generatedResult.career,   
        cards: generatedResult.cards,
        context_type: 'text',
      });
      setIsSaved(true);
      setSavedId(data.id);
      if (data.credits_remaining !== undefined) updateCredits(data.credits_remaining);
      toast.success('Guardado en tu historial');
      fetchHistory();
    } catch (e) {
      if (e.response?.status === 402) setShowPricing(true);
      else toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!generatedResult) return;
    if (user?.credits_remaining === -1) {
        toast.promise(exportToPDF(generatedResult), {
            loading: 'Generando PDF Premium...',
            success: '¡PDF descargado!',
            error: 'Error al generar el PDF.'
        });
        return;
    }
    try {
      const { data } = await api.post('/deduct-export');
      updateCredits(data.credits_remaining); 
      toast.promise(exportToPDF(generatedResult), {
          loading: 'Procesando...',
          success: '¡PDF descargado! (-1 crédito)',
          error: 'Error al generar el PDF.'
      });
    } catch (e) {
      if (e.response?.status === 402) setShowPricing(true);
      else toast.error('Créditos insuficientes.');
    }
  };

  const handleHistorySelect = (item) => {
    if (item.is_master_sequence) {
        setMsResultData(item.cards);
        setMsSavedId(item.id);
        setShowMSResult(true);
    } else {
        setGeneratedResult({ topic: item.topic, age: item.age, cards: item.cards, rating: item.rating });
        setIsSaved(true);
        setSavedId(item.id);
        setRewardsList(null);
    }
  };

  const handleDeleteHistory = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setHistory(prev => prev.filter(i => i.id !== id));
      toast.success('Eliminado');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleGenerateRewards = async () => {
    if (!generatedResult) return;
    const isGoldOrUltra = ['gold', 'ultra', 'institution_gold', 'institution_pro', 'institution_ultra'].includes(user?.plan);
    if (!isGoldOrUltra) {
      setShowPricing(true);
      return;
    }
    setIsGeneratingRewards(true);
    setShowRewardsModal(true);
    try {
      const { data } = await api.post('/generate/rewards', {
        topic: generatedResult.topic,
        age: generatedResult.age
      });
      setRewardsList(data.result.recompensas);
      if (data.credits_remaining !== undefined) updateCredits(data.credits_remaining);
    } catch (e) {
      setShowRewardsModal(false);
      if (e.response?.status === 402) setShowPricing(true);
      else toast.error('Error al generar recompensas.');
    } finally {
      setIsGeneratingRewards(false);
    }
  };

  const handleOpenMSConfig = (annualPlanId) => {
    setMsConfig({ ...msConfig, annual_plan_id: annualPlanId });
    setShowMSConfig(true);
  };

  const handleGenerateMasterSequence = async () => {
    if (!msConfig.subject.trim()) {
      toast.error('Por favor, ingresá la materia.');
      return;
    }
    setIsGeneratingMS(true);
    setMsSavedId(null); 
    try {
      const { data } = await api.post('/generate/master-sequence', msConfig);
      setMsResultData(data.result);
      setShowMSConfig(false);
      setShowMSResult(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al proyectar la Secuencia Maestra.');
    } finally {
      setIsGeneratingMS(false);
    }
  };

  const handleSaveMasterSequence = async () => {
    if (!msResultData || msSavedId) return;
    setIsSavingMS(true);
    try {
      const { data } = await api.post('/history/master-sequence', {
        topic: `Secuencia: ${msConfig.subject || msResultData.titulo || 'Trimestral'}`,
        age: msConfig.age || '14',
        subject: msConfig.subject || 'General',
        cards: msResultData
      });
      setMsSavedId(data.id);
      toast.success('¡Secuencia guardada en tu biblioteca!');
      fetchHistory();
    } catch (e) {
      toast.error('Error al guardar la secuencia.');
    } finally {
      setIsSavingMS(false);
    }
  };

  const getSemanasData = () => {
    if (!msResultData) return [];
    if (Array.isArray(msResultData)) return msResultData;
    const foundArray = Object.values(msResultData).find(val => Array.isArray(val));
    return foundArray || [];
  };

  const handleCopySequence = () => {
    if (!msResultData) return;
    const semanas = getSemanasData();
    let text = `SECUENCIA MAESTRA: ${msResultData.titulo || 'Proyectada'}\n\n`;
    
    semanas.forEach((s, idx) => {
      const modulo = s.modulo || s.semana || `Semana ${idx + 1}`;
      const tema = s.tema || s.titulo || 'Tema a definir';
      const objetivo = s.objetivo || s.descripcion || 'Sin especificar';
      const biblio = s.bibliografia_recomendada || s.bibliografia || 'Material general de la materia';
      
      text += `[${modulo}]\nTema: ${tema}\nObjetivo: ${objetivo}\nBibliografía Recomendada: ${biblio}\n\n`;
    });
    
    navigator.clipboard.writeText(text);
    toast.success('¡Secuencia copiada al portapapeles!');
  };

  const handleExportMSPDF = async () => {
    if (!msResultData) return;
    if (user?.credits_remaining === -1) {
        toast.promise(exportMSToPDF(msResultData, msConfig.subject), {
            loading: 'Generando PDF Premium...',
            success: '¡PDF descargado!',
            error: 'Error al generar el PDF.'
        });
        return;
    }
    try {
      const { data } = await api.post('/deduct-export');
      updateCredits(data.credits_remaining); 
      toast.promise(exportMSToPDF(msResultData, msConfig.subject), {
          loading: 'Procesando exportación...',
          success: '¡PDF descargado! (-1 crédito)',
          error: 'Error al generar el PDF.'
      });
    } catch (e) {
      if (e.response?.status === 402) setShowPricing(true);
      else toast.error('Créditos insuficientes.');
    }
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => ({ ...prev, [folderName]: !prev[folderName] }));
  };

  const handleRepoUpload = async (file, folderName = "General", isAnnual = false) => {
    if (!file) return;
    setIsUploadingRepo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const parseRes = await api.post('/upload-file', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const repoData = {
        folder: folderName.trim() || "General",
        is_annual_plan: isAnnual,
        file_name: file.name,
        content: parseRes.data.text
      };
      const { data } = await api.post('/repository', repoData);
      setRepoItems([{ ...repoData, id: data.id, created_at: new Date().toISOString() }, ...repoItems]);
      toast.success('Material guardado');
      setNewFolderName('');
    } catch (err) {
      toast.error('Error al guardar el archivo');
    } finally {
      setIsUploadingRepo(false);
    }
  };

  const handleDeleteRepoItem = async (id) => {
    try {
      await api.delete(`/repository/${id}`);
      setRepoItems(prev => prev.filter(i => i.id !== id));
      toast.success('Eliminado');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const groupedRepoItems = repoItems.reduce((acc, item) => {
    if (item.is_annual_plan) return acc; 
    const f = item.folder || "General";
    if (!acc[f]) acc[f] = [];
    acc[f].push(item);
    return acc;
  }, {});
  
  const annualPlans = repoItems.filter(item => item.is_annual_plan);
  const availableFolders = Object.keys(groupedRepoItems).length > 0 ? Object.keys(groupedRepoItems) : ['General'];
  
  const semanasData = getSemanasData();

  return (
    <div className="flex flex-col h-[100dvh] bg-[#030712] relative overflow-hidden font-sans">
      
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={false} 
        disableOverlayClose={true}
        spotlightClicks={true}
        disableScrolling={true} 
        callback={handleJoyrideCallback}
        locale={{
          back: 'Atrás',
          close: 'Entendido',
          last: '¡Empezar ahora!',
          next: 'Siguiente',
          skip: 'Saltar tutorial'
        }}
        styles={{
          options: {
            primaryColor: '#3b82f6', 
            backgroundColor: '#0f172a',
            textColor: '#f8fafc',
            arrowColor: '#0f172a',
            overlayColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: '20px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
            padding: '10px',
          },
          tooltipContainer: {
            textAlign: 'left',
            fontFamily: 'system-ui, sans-serif',
          },
          buttonNext: {
            backgroundColor: '#2563eb',
            borderRadius: '12px',
            fontWeight: '900',
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '10px 20px',
          },
          buttonBack: {
            color: '#94a3b8',
            fontWeight: 'bold',
            marginRight: '10px'
          },
          buttonSkip: {
            color: '#64748b',
            fontSize: '11px',
            fontWeight: 'bold'
          }
        }}
      />

      <Header 
        onShowPricing={() => setShowPricing(true)} 
        onStartTour={() => {
          localStorage.removeItem('dinamiclass_tour_completed');
          setRunTour(true);
        }}
      />

      <div className="flex flex-1 overflow-hidden relative">
        <Sidebar
          items={history}
          loading={historyLoading}
          onSelect={handleHistorySelect}
          onDelete={handleDeleteHistory}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="flex-1 overflow-y-auto pb-32 z-10 custom-scrollbar w-full">
          <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 w-full">
            
            <div className="lg:hidden mt-2">
              <button onClick={() => setIsSidebarOpen(true)} className="w-full flex items-center justify-center gap-2 bg-slate-900 border border-slate-800 text-slate-300 py-3 rounded-2xl font-bold text-sm shadow-sm transition-active active:scale-[0.98]">
                <Library size={18} className="text-blue-400" /> Mi biblioteca de clases preparadas
              </button>
            </div>

            <div className="animate-fade-in-up">
              <QuoteOfTheDay />
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mt-4">
                {user?.name?.split(' ')[0] || 'Docente'}, ¿qué clase vas a dinamizar?
              </h1>
              <p className="text-slate-400 mt-2 text-xs sm:text-sm font-medium">Ingresá el tema y personalizá tu planificación.</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-6 animate-fade-in-up w-full tour-step-1">
              <GenerationForm
                onGenerate={handleGenerate}
                isLoading={isGenerating}
                user={user}
                onShowPricing={() => setShowPricing(true)}
                onCreditsUpdate={updateCredits}
                onOpenUltraPromo={() => setShowUltraPromo(true)} 
              />
            </div>

            {generatedResult && !isGenerating && (
                <div className="flex justify-end animate-fade-in-up">
                    <button onClick={handleGenerateRewards} className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white px-6 py-3.5 sm:py-3 rounded-xl font-bold text-sm shadow-lg transition-all active:scale-[0.98]">
                        <Gift size={18} /> Crear Sistema de Recompensas
                    </button>
                </div>
            )}

            {generatedResult && (
              <GeneratedCards
                key={savedId || 'unsaved'}
                result={generatedResult}
                onSave={handleSave}
                isSaving={isSaving}
                isSaved={isSaved}
                savedId={savedId}
                onExportPDF={handleExportPDF}
              />
            )}

            <div className="tour-step-4 mt-6">
                {/* ¡ACÁ ESTÁ EL CAMBIO CRUCIAL! 
                  Se sacó el {generatedResult && (...)} para que las GoldFeatures siempre se vean abajo.
                */}
                <GoldFeatures
                    user={user}
                    onShowPricing={() => setShowPricing(true)}
                    onCreditsUpdate={updateCredits}
                    generatedResult={generatedResult}
                    onOpenUltraPromo={() => setShowUltraPromo(true)}
                />
            </div>

          </div>
        </main>

        <div className={`fixed inset-y-0 right-0 w-full md:w-96 bg-slate-950 border-l border-slate-800 z-[70] transform transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-2xl flex flex-col ${isRepoOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="p-4 sm:p-6 border-b border-slate-800 flex justify-between items-center bg-gradient-to-r from-slate-950 to-blue-950/20">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600/20 rounded-lg border border-blue-500/30">
                        <Library className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-white leading-tight">Repositorio <span className="text-blue-400">Ultra</span></h2>
                        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider text-left">Tu cerebro bibliográfico</p>
                    </div>
                </div>
                <button onClick={() => setIsRepoOpen(false)} className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors">
                    <X size={20} strokeWidth={2.5}/>
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 sm:space-y-8 custom-scrollbar">
                <div className="space-y-3">
                    <h3 className="text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <Crown size={14} className="text-amber-400" /> Documentos Rectores
                    </h3>
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-inner relative overflow-hidden">
                        {annualPlans.length > 0 ? (
                            <div className="space-y-2">
                                {annualPlans.map(item => (
                                    <div key={item.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 p-3 rounded-lg group hover:border-amber-500/30 transition-all">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <FileText size={16} className="text-amber-400 shrink-0" />
                                            <span className="text-xs sm:text-sm text-slate-300 truncate font-medium">{item.file_name}</span>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button 
                                                onClick={() => handleOpenMSConfig(item.id)} 
                                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 p-1.5 rounded-md transition-all shrink-0 border border-transparent hover:border-blue-500/30"
                                                title="Proyectar Secuencia Maestra"
                                            >
                                                <Wand2 size={14} />
                                            </button>
                                            <button onClick={() => handleDeleteRepoItem(item.id)} className="text-slate-500 hover:text-red-400 p-1.5 rounded-md transition-all shrink-0" title="Eliminar">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-[11px] sm:text-xs text-slate-500 mb-3 text-left">Subí tu Planificación Anual para proyectar tu Secuencia Maestra.</p>
                            </div>
                        )}
                        <button 
                            onClick={() => { repoFileRef.current.dataset.isAnnual = "true"; repoFileRef.current.click(); }} 
                            className="mt-2 w-full py-2.5 sm:py-2 border border-dashed border-slate-700 hover:border-amber-500/50 text-slate-400 hover:text-amber-400 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                        >
                            <UploadCloud size={14} /> Subir Planificación
                        </button>
                    </div>
                </div>

                <div className="w-full h-px bg-slate-800"></div>

                <div className="space-y-3 text-left">
                    <h3 className="text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <FolderOpen size={14} className="text-blue-400" /> Tus Materias
                    </h3>
                    <div className="flex gap-2 mb-4">
                        <input 
                            type="text" 
                            placeholder="Nueva carpeta..." 
                            value={newFolderName}
                            onChange={e => setNewFolderName(e.target.value)}
                            className="flex-1 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-blue-500 w-full"
                        />
                        <button 
                            onClick={() => { repoFileRef.current.dataset.folder = newFolderName; repoFileRef.current.dataset.isAnnual = "false"; repoFileRef.current.click(); }}
                            disabled={!newFolderName.trim() || isUploadingRepo}
                            className="px-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg transition-all shrink-0"
                        >
                            <FolderPlus size={16} />
                        </button>
                    </div>
                    {Object.keys(groupedRepoItems).length > 0 && Object.entries(groupedRepoItems).map(([folderName, items]) => (
                        <div key={folderName} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden mb-2">
                            <button onClick={() => toggleFolder(folderName)} className="w-full flex items-center justify-between p-3 bg-slate-950/50 hover:bg-slate-800 transition-colors">
                                <span className="text-xs sm:text-sm font-bold text-slate-300 flex items-center gap-1.5"><ChevronRight size={14} className={expandedFolders[folderName] ? 'rotate-90' : ''}/> {folderName}</span>
                                <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">{items.length}</span>
                            </button>
                            {expandedFolders[folderName] && (
                                <div className="p-3 space-y-2 bg-slate-900">
                                    {items.map(item => (
                                        <div key={item.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 p-2.5 rounded-lg group">
                                            <span className="text-[11px] sm:text-xs text-slate-400 truncate">{item.file_name}</span>
                                            <button onClick={() => handleDeleteRepoItem(item.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                    <button 
                                        onClick={() => { repoFileRef.current.dataset.folder = folderName; repoFileRef.current.dataset.isAnnual = "false"; repoFileRef.current.click(); }} 
                                        className="w-full py-2.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 border border-dashed border-slate-700 hover:border-blue-500/50 text-xs font-semibold flex items-center justify-center gap-1.5 rounded-lg transition-all active:scale-[0.98] mt-2"
                                    >
                                        <Plus size={14} /> Añadir apunte
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            
            {isUploadingRepo && (
                <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm flex flex-col items-center justify-center z-50">
                    <Loader2 className="text-blue-500 w-8 h-8 animate-spin mb-3" />
                    <p className="text-sm font-bold text-white tracking-tight">Procesando material...</p>
                </div>
            )}
        </div>

        <input 
            ref={repoFileRef} 
            type="file" 
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.xls,.csv,.txt,.jpg,.jpeg,.png" 
            className="hidden" 
            onChange={e => { 
                const f = e.target.files?.[0]; 
                const isAnn = repoFileRef.current.dataset.isAnnual === "true";
                const fold = repoFileRef.current.dataset.folder || "General";
                if (f) handleRepoUpload(f, fold, isAnn); 
                e.target.value = ''; 
            }} 
        />

        {isRepoOpen && (
            <div onClick={() => setIsRepoOpen(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[65] md:hidden transition-opacity" />
        )}
      </div>

      <div className={`fixed top-1/2 -translate-y-1/2 right-0 z-[60] flex flex-col items-end tour-step-5`}>
        {!isUltra ? (
            <button 
                onClick={() => setShowUltraPromo(true)}
                className="group relative flex items-center justify-center py-4 px-1.5 sm:px-2 bg-slate-950 border border-r-0 border-blue-500/50 rounded-l-xl shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:pr-4 transition-all duration-300 ease-out overflow-hidden"
            >
                <div className="absolute inset-0 bg-gradient-to-b from-blue-600/20 via-transparent to-blue-600/20 opacity-50 group-hover:opacity-100 transition-opacity" />
                <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }} className="text-[9px] sm:text-[10px] font-black text-blue-400 tracking-[0.2em] uppercase rotate-180 drop-shadow-[0_0_5px_rgba(59,130,246,0.8)]">
                    Desbloquear Ultra
                </span>
                <Rocket size={12} className="text-blue-400 mt-2 rotate-90 sm:w-3.5 sm:h-3.5" />
            </button>
        ) : (
            <button 
                onClick={() => setIsRepoOpen(true)}
                className="flex flex-col items-center justify-center py-5 px-1.5 sm:px-2 bg-slate-900 border border-r-0 border-slate-700 hover:border-blue-500 hover:bg-slate-800 rounded-l-xl shadow-xl transition-all duration-300 ease-out active:scale-95"
            >
                <Library size={14} className="text-blue-400 mb-3 sm:w-4 sm:h-4" />
                <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }} className="text-[10px] sm:text-[11px] font-bold text-slate-300 tracking-[0.1em] uppercase rotate-180">
                    Repositorio
                </span>
            </button>
        )}
      </div>

      <div className={`fixed bottom-6 right-4 md:right-8 flex flex-col gap-3 z-[50] transition-transform duration-500 ${isRepoOpen ? 'md:translate-x-[-384px]' : 'translate-x-0'}`}>
        <button
          onClick={() => setShowShare(true)}
          className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-[0_5px_15px_rgba(37,99,235,0.4)] hover:bg-blue-500 transition-all hover:scale-110 active:scale-95 border border-blue-400/30"
          title="Compartir DinamiClass"
        >
          <Share2 size={20} className="sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={() => setShowSupport(true)}
          className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-800 text-slate-300 rounded-full flex items-center justify-center shadow-[0_5px_15px_rgba(0,0,0,0.5)] hover:bg-slate-700 hover:text-white border border-slate-700 transition-all hover:scale-110 active:scale-95"
          title="Soporte y Ayuda"
        >
          <LifeBuoy size={20} className="sm:w-6 sm:h-6" />
        </button>
      </div>

      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} currentPlan={user?.plan} />
      <SupportModal open={showSupport} onClose={() => setShowSupport(false)} />
      <ShareModal open={showShare} onClose={() => setShowShare(false)} />
      <Toaster theme="dark" position="bottom-right" richColors />

      {/* MODAL: Configurar Secuencia Maestra */}
      <Dialog open={showMSConfig} onOpenChange={setShowMSConfig}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md w-[95vw] p-6 shadow-2xl z-[120]">
            <DialogTitle className="text-xl font-bold flex items-center gap-2 mb-4">
                <Wand2 className="text-blue-400" /> Proyectar Secuencia Maestra
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-400 mb-6">
                Indicá la materia exacta y qué carpeta de apuntes querés cruzar con esta planificación anual.
            </DialogDescription>
            <div className="space-y-4">
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Materia a proyectar</label>
                    <input 
                        type="text" 
                        value={msConfig.subject} 
                        onChange={(e) => setMsConfig({...msConfig, subject: e.target.value})}
                        placeholder="Ej: Historia, Prácticas del Lenguaje..." 
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Edad de los alumnos</label>
                    <select 
                        value={msConfig.age} 
                        onChange={(e) => setMsConfig({...msConfig, age: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all text-sm appearance-none"
                    >
                        {AGES_MS.map(a => <option key={a} value={a}>{a} años</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5 block flex items-center gap-1">
                        <FolderOpen size={14} className="text-amber-400"/> Carpeta de Bibliografía
                    </label>
                    <select 
                        value={msConfig.folder_name} 
                        onChange={(e) => setMsConfig({...msConfig, folder_name: e.target.value})}
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all text-sm appearance-none"
                    >
                        {availableFolders.map(folder => <option key={folder} value={folder}>{folder}</option>)}
                    </select>
                </div>
            </div>
            <div className="mt-8 flex gap-3">
                <button onClick={() => setShowMSConfig(false)} className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-bold transition-all">Cancelar</button>
                <button 
                    onClick={handleGenerateMasterSequence} 
                    disabled={isGeneratingMS || !msConfig.subject.trim()}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                >
                    {isGeneratingMS ? <><Loader2 size={16} className="animate-spin" /> Procesando</> : 'Proyectar'}
                </button>
            </div>
        </DialogContent>
      </Dialog>

      {/* MODAL: Resultado de la Secuencia Maestra */}
      <Dialog open={showMSResult} onOpenChange={setShowMSResult}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl w-[95vw] max-h-[85vh] p-0 flex flex-col shadow-2xl z-[120] [&>button]:hidden">
            <div className="p-4 sm:p-6 border-b border-slate-800 bg-slate-950 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600/20 rounded-lg border border-blue-500/30">
                        <Wand2 className="text-blue-400" size={20} />
                    </div>
                    <div>
                        <DialogTitle className="text-lg sm:text-xl font-bold leading-tight">{msResultData?.titulo || 'Secuencia Maestra'}</DialogTitle>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Basado en tu planificación anual</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleExportMSPDF} className="p-2 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 hover:text-blue-300 rounded-lg transition-all shadow-sm flex items-center gap-2" title="Descargar PDF">
                        <Download size={16} /> <span className="hidden sm:block text-xs font-bold">PDF</span>
                    </button>
                    <button onClick={handleCopySequence} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 hover:text-white transition-all shadow-sm flex items-center gap-2" title="Copiar texto">
                        <Copy size={16} /> <span className="hidden sm:block text-xs font-bold">Copiar</span>
                    </button>
                    <button onClick={() => setShowMSResult(false)} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
                        <X size={18} strokeWidth={2.5}/>
                    </button>
                </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-1 bg-[#0a0f1c]">
                <div className="space-y-4">
                    {semanasData.length > 0 ? (
                        semanasData.map((sem, idx) => (
                            <div key={idx} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 sm:p-5 relative overflow-hidden group hover:border-blue-500/30 transition-colors text-left">
                                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50" />
                                <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2 flex items-center gap-1.5"><CalendarClock size={12}/> {sem.modulo || sem.semana || `Semana ${idx + 1}`}</h4>
                                <h3 className="text-base sm:text-lg font-bold text-white mb-2">{sem.tema || sem.titulo || 'Tema a definir'}</h3>
                                <div className="space-y-3 mt-3 text-sm">
                                    <div className="bg-slate-950 rounded-lg p-3 border border-slate-800/50">
                                        <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1"><Rocket size={12}/> Objetivo</p>
                                        <p className="text-slate-300 leading-relaxed">{sem.objetivo || sem.descripcion || 'Sin objetivo definido'}</p>
                                    </div>
                                    <div className="bg-slate-950 rounded-lg p-3 border border-slate-800/50">
                                        <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1"><BookMarked size={12}/> Bibliografía de Apoyo</p>
                                        <p className="text-slate-400 italic leading-relaxed">{sem.bibliografia_recomendada || sem.bibliografia || 'Material general de la materia'}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-10 px-4 bg-slate-900/50 border border-dashed border-slate-700 rounded-xl">
                            <Wand2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                            <p className="text-sm text-slate-300 font-bold mb-1">Estructura vacía o ilegible</p>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                El sistema no detectó contenido extraíble en el documento. Si tu PDF es una imagen escaneada o fotos de un libro, intentalo subiendo un documento Word o un PDF que contenga texto real.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="p-4 bg-slate-950 border-t border-slate-800 flex justify-end gap-3 shrink-0">
                <button 
                    onClick={handleSaveMasterSequence} 
                    disabled={isSavingMS || msSavedId}
                    className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-all shadow-lg flex items-center gap-2"
                >
                    {isSavingMS ? <><Loader2 size={16} className="animate-spin" /> Guardando</> : msSavedId ? <><BookMarked size={16} className="text-blue-400" /> Guardado</> : <><Save size={16} /> Guardar en Biblioteca</>}
                </button>
                <button onClick={() => setShowMSResult(false)} className="px-8 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg active:scale-95 w-full sm:w-auto">
                    Aceptar
                </button>
            </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUltraPromo} onOpenChange={setShowUltraPromo}>
        <DialogContent className="bg-[#0a0f1c] border border-blue-500/30 text-white max-w-md w-[95vw] p-0 shadow-[0_0_50px_rgba(59,130,246,0.15)] overflow-hidden [&>button]:hidden z-[110]">
            <div className="relative p-6 sm:p-8 flex flex-col items-center text-center">
                <button onClick={() => setShowUltraPromo(false)} className="absolute top-4 right-4 p-2 text-slate-500 hover:text-white transition-colors rounded-lg hover:bg-slate-800/50">
                  <X size={20} />
                </button>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center mb-5 sm:mb-6 shadow-[0_0_30px_rgba(59,130,246,0.4)]">
                    <Library size={28} className="text-white sm:w-8 sm:h-8" />
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-white mb-2 leading-tight">
                    El cerebro de tus clases,<br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">totalmente automatizado.</span>
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mb-6 sm:mb-8 leading-relaxed text-center">
                    Subí tu Planificación Anual y armá tu biblioteca privada. 
                    El sistema conectará todos tus apuntes para armarte las clases del trimestre en segundos.
                </p>
                <button 
                    onClick={() => { setShowUltraPromo(false); setShowPricing(true); }}
                    className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-black uppercase tracking-widest text-[11px] sm:text-xs rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all active:scale-[0.98]"
                >
                    Actualizar a Plan Ultra
                </button>
            </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRewardsModal} onOpenChange={setShowRewardsModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg w-[95vw] p-0 flex flex-col z-[100] [&>button]:hidden shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-slate-800 bg-slate-950 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-3">
                    <Gift className="text-amber-500 sm:w-6 sm:h-6" size={20} />
                    <DialogTitle className="text-lg sm:text-xl font-bold">Premios y Recompensas</DialogTitle>
                </div>
                <button onClick={() => setShowRewardsModal(false)} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
                  <X size={18} strokeWidth={2.5}/>
                </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh] custom-scrollbar">
                {isGeneratingRewards ? (
                    <div className="flex flex-col items-center justify-center py-10">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-4" />
                        <p className="text-sm text-slate-400 animate-pulse text-center">Diseñando recompensas motivadoras...</p>
                    </div>
                ) : rewardsList?.map((reward, index) => (
                    <div key={index} className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 sm:p-4 mb-3 flex gap-3 sm:gap-4 hover:border-amber-500/30 transition-all text-left">
                        <div className="bg-slate-950 text-slate-400 font-black rounded-lg w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center shrink-0 border border-slate-800 text-xs sm:text-base">{index + 1}</div>
                        <div><h4 className="font-bold text-amber-400 mb-1 text-sm sm:text-base">{reward.titulo}</h4><p className="text-xs sm:text-sm text-slate-300 leading-relaxed">{reward.descripcion}</p></div>
                    </div>
                ))}
            </div>
            <div className="p-4 bg-slate-950 border-t border-slate-800 flex justify-end shrink-0">
                <button onClick={() => setShowRewardsModal(false)} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-bold transition-colors w-full sm:w-auto">Cerrar</button>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
